import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getFailureDetail } from "../constants/testDetailMap";
import { getSampleRowsWithIds } from "../utils/rowSampleData";
import "./TestDetailPage.css";

const TAB_ERROR = "error";
const TAB_DATA = "data";

function Chevron({ open }) {
  return (
    <svg className={`detail-card__chevron ${open ? "detail-card__chevron--open" : ""}`} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function GearIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 15.5a3.5 3.5 0 100-7 3.5 3.5 0 000 7z"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.6a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9c.47 0 .89.18 1.2.49H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"
        stroke="currentColor"
        strokeWidth="1.2"
      />
    </svg>
  );
}

function FaultBitCard({ card, initiallyOpen = false, stableKey = "" }) {
  const [open, setOpen] = useState(initiallyOpen);
  const title = String(card.title).toUpperCase();
  const slug = `${String(card.bitId).replace(/\W+/g, "-")}${stableKey ? `-${stableKey}` : ""}`;

  return (
    <section className={`detail-card fault-bit-card ${open ? "" : "fault-bit-card--collapsed"}`}>
      <button
        type="button"
        className="detail-card__trigger fault-bit-card__toggle"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-controls={`fault-body-${slug}`}
        id={`fault-head-${slug}`}
      >
        <div className="detail-card__icon" aria-hidden="true">{card.bitId}</div>
        <div className="detail-card__text">
          <h2 className="detail-card__name">{title}</h2>
          {card.subtitle ? <p className="detail-card__sub">{card.subtitle}</p> : null}
        </div>
        <Chevron open={open} />
      </button>
      {open && (
        <div
          className="detail-card__body fault-bit-card__body"
          id={`fault-body-${slug}`}
          role="region"
          aria-labelledby={`fault-head-${slug}`}
        >
          <div className="detail-section">
            <p className="detail-section__label">
              <span className="detail-section__dot" aria-hidden />
              TROUBLESHOOT ACTION
            </p>
            <ol className="detail-section__list">
              {card.troubleshootSteps.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ol>
          </div>
          <div>
            <p className="detail-service-label">
              <GearIcon /> SERVICE ACTION
            </p>
            <p className="detail-service-box">{card.serviceAction}</p>
          </div>
        </div>
      )}
    </section>
  );
}

export default function TestDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const row = location.state?.row;
  /** Error tab meaningful only when at least one error bit is set */
  const [activeTab, setActiveTab] = useState(TAB_DATA);

  if (!row) {
    return (
      <div className="detail-root" style={{ padding: 24 }}>
        <p style={{ marginBottom: 16 }}>No test selected.</p>
        <button type="button" className="detail-back" onClick={() => navigate(-1)} aria-label="Back">
          ←
        </button>
      </div>
    );
  }

  const detail = getFailureDetail(row);
  const cards = Array.isArray(detail.faultCards) && detail.faultCards.length > 0 ? detail.faultCards : null;
  const sampleRows = getSampleRowsWithIds(row);

  const showFaultBits = detail.showErrorCodeBadge === true;
  const hasDataSamples = sampleRows.length > 0;
  const showDualTabs = showFaultBits && hasDataSamples;

  useEffect(() => {
    setActiveTab(showFaultBits ? TAB_ERROR : TAB_DATA);
  }, [showFaultBits, row?.testid, row?.time]);

  const faultDescriptionContent =
    cards != null ? (
      cards.map((card, i) => (
        <FaultBitCard key={`${card.bitId}-${i}`} card={card} stableKey={String(i)} initiallyOpen={i === 0} />
      ))
    ) : (
      <FaultBitCard
        card={{
          bitId: detail.primaryCheckId ?? "ERR",
          title: (detail.primaryProbeTitle ?? detail.testName).replace(/_/g, " "),
          subtitle: detail.categorySubtitle,
          troubleshootSteps: detail.troubleshootSteps,
          serviceAction: detail.serviceAction,
        }}
        initiallyOpen
      />
    );

  const dataPaneContent =
    sampleRows.length === 0 ? (
      <p className="detail-data-empty">No sample data array in this payload. Raw values may be embedded only in the error code string.</p>
    ) : (
      <div className="detail-data-scroll">
        <table className="detail-data-table">
          <thead>
            <tr>
              <th className="detail-data-table__id" scope="col">
                ID
              </th>
              <th className="detail-data-table__val-header" scope="col">
                Value
              </th>
            </tr>
          </thead>
          <tbody>
            {sampleRows.map((r, i) => (
              <tr key={`${String(r.id)}-${i}`}>
                <td className="detail-data-table__id">{r.id}</td>
                <td className="detail-data-table__val">{r.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );

  return (
    <div className="detail-root">
      <header className="detail-header">
        <button type="button" className="detail-back" onClick={() => navigate(-1)} aria-label="Back">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
        <div className="detail-title-block">
          <h1 className="detail-title">{detail.moduleTitle}</h1>
          {detail.showErrorCodeBadge ? (
            <div className="detail-error-badge">ERROR_CODE: {detail.errorHex}</div>
          ) : null}
        </div>
      </header>

      <p className="detail-sensor-line">{detail.sensorLine}</p>

      {!showFaultBits && !hasDataSamples && (
        <div className="detail-data-wrap" style={{ paddingTop: 8 }}>
          <p className="detail-data-empty">No error bits reported and no sample data in this payload.</p>
        </div>
      )}

      {showDualTabs ? (
        <>
          <div className="detail-tabs" role="tablist" aria-label="Diagnostic detail">
            <button
              type="button"
              role="tab"
              id={`tab-${TAB_ERROR}`}
              aria-selected={activeTab === TAB_ERROR}
              aria-controls={`panel-${TAB_ERROR}`}
              className={`detail-tabs__btn ${activeTab === TAB_ERROR ? "detail-tabs__btn--active" : ""}`}
              onClick={() => setActiveTab(TAB_ERROR)}
            >
              Error code description
            </button>
            <button
              type="button"
              role="tab"
              id={`tab-${TAB_DATA}`}
              aria-selected={activeTab === TAB_DATA}
              aria-controls={`panel-${TAB_DATA}`}
              className={`detail-tabs__btn ${activeTab === TAB_DATA ? "detail-tabs__btn--active" : ""}`}
              onClick={() => setActiveTab(TAB_DATA)}
            >
              Data
            </button>
          </div>
          <div
            id={`panel-${TAB_ERROR}`}
            role="tabpanel"
            aria-labelledby={`tab-${TAB_ERROR}`}
            hidden={activeTab !== TAB_ERROR}
            className="detail-tab-panel"
          >
            <div className="detail-list">{faultDescriptionContent}</div>
          </div>
          <div
            id={`panel-${TAB_DATA}`}
            role="tabpanel"
            aria-labelledby={`tab-${TAB_DATA}`}
            hidden={activeTab !== TAB_DATA}
            className="detail-tab-panel"
          >
            <div className="detail-data-wrap">{dataPaneContent}</div>
          </div>
        </>
      ) : null}

      {showFaultBits && !hasDataSamples ? (
        <div className="detail-tab-panel">
          <div className="detail-list">{faultDescriptionContent}</div>
        </div>
      ) : null}

      {!showFaultBits && hasDataSamples ? (
        <div className="detail-tab-panel">
          <div className="detail-data-wrap">{dataPaneContent}</div>
        </div>
      ) : null}
    </div>
  );
}
